import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { handlePushNotificationSubscription } from '../utils/pushNotificationUtils';
import Modal from 'react-modal';
import SettingsModal from '../modals/SettingsModal';
import NewRequestModal from '../modals/NewRequestModal';
import RequestList from '../components/RequestList';
import ServiceMap from '../components/ServiceMap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '../utils/icons';
import { useUserLocation } from '../utils/locationUtils';
import { updateUserLocation } from '../api/user';
import PullToRefreshWrapper from '../components/PullToRefreshWrapper';

const Main = () => {
  const { language, translations } = useLanguage();
  const { user } = useUser();
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isNewRequestModalOpen, setNewRequestModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const {userLatitude, userLongitude } = useUserLocation();
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [activeTab, setActiveTab] = useState("service-map");

  // Ask for push-notification permission
  useEffect(() => {
    if (user) {
      handlePushNotificationSubscription();
    }
  }, [user]);

  useEffect(() => {
    if (user !== undefined && user !== null) {
      setActiveTab("requests");
    }
  }, [user]);

  // Update user's location to database when Main component is mounted
  useEffect(() => {
    const alreadyUpdated = sessionStorage.getItem("locationUpdated");
    if (user && userLatitude && userLongitude && !alreadyUpdated) {
      updateUserLocation(userLatitude, userLongitude);
      sessionStorage.setItem("locationUpdated", "true");
    };
  }, [user, userLatitude, userLongitude]);

  const logos: { [key: string]: string } = {
    finnish: '/banner_fi.jpg',
    english: '/banner_en.jpg',
    swedish: '/banner_sv.jpg'
  };
  const logoUrl = logos[language] || logos.fi;

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const triggerRefresh = useCallback(() => {
    return new Promise<void>((resolve) => {
      setRefreshTrigger(prev => {
        resolve();
        return !prev;
      });
    });
  }, []);

  return (
    <div className='main'>
      <div className="container">
        <div className="top-bar">
          <img src={logoUrl} className='main-page-logo' alt='Main Page Logo'></img>
          <div className="settings" onClick={() => setSettingsModalOpen(true)}><FontAwesomeIcon icon={icons.bars}/></div>
        </div>
        <div className='top-bar-sides'></div>
        <div className='tab-container'>
          <button 
            className={`tab ${activeTab === 'requests' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            {translations.requests}
          </button>
          <button 
            className={`tab ${activeTab === 'service-map' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('service-map')}
          >
            {translations.servicemap.servicemap}
          </button>
        </div>
        <div className="tab-content-wrapper">
          <div className={`tab-panel ${activeTab === 'requests' ? 'visible' : 'hidden'}`}>
            <PullToRefreshWrapper className={'content'} onRefresh={triggerRefresh}>
              <div className={`requests-header ${user == null ? 'disabled' : ''}`}>
                <label className="checkbox-container">
                  <input type="checkbox" name="showAll" checked={showAll} onChange={toggleShowAll} disabled={user == null}/>
                  <div className='checkmark show-all'></div>
                  <p className='show-all-text'>{translations.showall}</p>
                </label>
              </div>
              <RequestList showAll={showAll} refreshTrigger={refreshTrigger}/>
            </PullToRefreshWrapper>
            <div className="bottom">
              {user && (
                <button 
                  className={`big-button new-request ${user == null ? 'disabled' : ''}`}
                  onClick={() => setNewRequestModalOpen(true)}
                  disabled={user == null}
                >
                  <FontAwesomeIcon icon={icons.plus}/> {translations.newrequest}
                </button>
              )} 
            </div>
          </div>
          <div className={`tab-panel ${activeTab === 'service-map' ? 'visible' : 'hidden'}`}>
            <ServiceMap />
          </div>
        </div>
      </div>

      <Modal 
        isOpen={isSettingsModalOpen} 
        onRequestClose={() => setSettingsModalOpen(false)} 
        ariaHideApp={false} 
        className="modal"  
      >
        <SettingsModal onClose={() => setSettingsModalOpen(false)} />
      </Modal>
      <Modal 
        isOpen={isNewRequestModalOpen} 
        onRequestClose={() => setNewRequestModalOpen(false)} 
        ariaHideApp={false} 
        className="modal"  
      >
        <NewRequestModal 
          onClose={() => setNewRequestModalOpen(false)}
          triggerRefresh={triggerRefresh}
         />
      </Modal>
    </div>
  );
}

export default Main;
